import { useMemo } from "react";
import { validator } from "@/utils/validator";
import { MAX_CHARACTER_INPUT_VALIDATION, MAX_TEXT_INPUT_VALIDATION } from "@/constants/common";
import { dayjs } from "@/utils/dayjs";
import { PATTERN } from "@/constants/pattern";
import { Dayjs } from "dayjs";
import { ASIA_TOKYO_TIMEZONE, DATE_FORMAT_FOR_API } from "@/constants/datetime";

const getBrowserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return ASIA_TOKYO_TIMEZONE;
  }
};

const normalizeDate = (value: string | number | Dayjs | Date | null | undefined) => {
  if (!value) return null;
  const tz = getBrowserTimezone();

  if (dayjs.isDayjs(value)) {
    return value.tz(tz).startOf("day");
  }

  if (typeof value === "string") {
    const tokyoDate = dayjs(value).tz(ASIA_TOKYO_TIMEZONE);
    const dateStr = tokyoDate.format(DATE_FORMAT_FOR_API);
    return dayjs(dateStr, DATE_FORMAT_FOR_API).startOf("day");
  }

  return dayjs(value).tz(tz).startOf("day");
};

export const useCaseScheduleSchema = () => {
  const caseScheduleSchema = useMemo(() => {
    return validator.object().shape({
      dateTimeTalent: validator
        .string()
        .emptyToNull()
        .nullable()
        .label("面接に関する詳細（候補日時、実地面接詳細等）")
        .max(MAX_TEXT_INPUT_VALIDATION)
        .when("isConfirm", {
          is: true,
          then: (schema) => schema.notRequired(),
          otherwise: (schema) => schema.required(),
        }),
      talentIds: validator.array().nullable().optional().notRequired(),
      isConfirm: validator.boolean().nullable(),
      dateConfirm: validator
        .string()
        .emptyToNull()
        .nullable()
        .label("面接日")
        .optional()
        .max(MAX_CHARACTER_INPUT_VALIDATION)
        .when("isConfirm", {
          is: true,
          then: (schema) => schema.required(),
          otherwise: (schema) => schema.notRequired(),
        })
        .test("date-not-past", "面接日は今日以降の日付を選択してください。", function (value) {
          const { parent } = this;
          if (!parent.isConfirm || !value) return true;

          const selectedDate = normalizeDate(value);
          if (!selectedDate || !selectedDate.isValid()) return true;

          const today = dayjs().tz(getBrowserTimezone()).startOf("day");

          if (parent.originalDateConfirm) {
            const originalDate = normalizeDate(parent.originalDateConfirm);
            if (selectedDate.isSame(originalDate, "day")) {
              return true;
            }
          }

          return selectedDate.isSame(today, "day") || selectedDate.isAfter(today, "day");
        })
        .test("date-max-year", "「面接日」は2199年より前にする必要があります。", function (value) {
          const { parent } = this;
          if (!parent.isConfirm || !value) return true;

          const selectedDate = normalizeDate(value);
          if (!selectedDate || !selectedDate.isValid()) return true;

          return selectedDate.year() <= 2199;
        }),

      startTimeConfirm: validator
        .string()
        .emptyToNull()
        .nullable()
        .label("面接開始時間")
        .max(MAX_CHARACTER_INPUT_VALIDATION)
        .when("isConfirm", {
          is: true,
          then: (schema) => schema.required(),
          otherwise: (schema) => schema.notRequired(),
        })
        .test("start-time-future", "面接開始時間は現在時刻より後に設定してください。", function (value) {
          const { parent } = this;
          if (!parent.isConfirm || !value || !parent.dateConfirm) return true;

          const isEditMode = !!parent.originalDateConfirm && !!parent.originalStartTimeConfirm;

          const selectedDate = normalizeDate(parent.dateConfirm);
          const originalDate = normalizeDate(parent.originalDateConfirm);

          if (!selectedDate) return true;

          const dateUnchanged = originalDate && selectedDate.isSame(originalDate, "day");
          const timeUnchanged = value === parent.originalStartTimeConfirm;

          if (isEditMode && dateUnchanged && timeUnchanged) {
            return true;
          }

          const today = dayjs().startOf("day");

          if (!selectedDate.isSame(today, "day")) return true;

          const now = dayjs();
          const [hours, minutes] = value.split(":").map(Number);
          const startTime = selectedDate.add(hours, "hour").add(minutes, "minute");

          return startTime.isAfter(now);
        }),
      interviewMethod: validator.number().nullable().label("実施方法").notRequired(),
      meetingToolType: validator.number().nullable().label("オンライン会議ツール").notRequired(),
      meetingId: validator.string().emptyToNull().nullable().label("ミーティングID").max(255).notRequired(),
      passcode: validator.string().emptyToNull().nullable().label("パスコード").max(255).notRequired(),
      url: validator
        .string()
        .trim()
        .emptyToNull()
        .matches(PATTERN.CUSTOM_URL, "正しいURL形式で入力してください。")
        .nullable()
        .label("面接URL")
        .max(MAX_TEXT_INPUT_VALIDATION),
      originalDateConfirm: validator.string().nullable().optional().notRequired(),
      originalStartTimeConfirm: validator.string().nullable().optional().notRequired(),
    });
  }, []);

  return { caseScheduleSchema };
};
